/**
 * Staged timber-RFQ releases grouped the way staff think about them.
 *
 * `releaseVersionIndex` is one counter per RFQ work order, so staging the Feed Barn,
 * Equipment Barn and Sales Barn lists separately yields v1 / v2 / v3 even though they
 * are three different lists. This module regroups releases by *structure lane* (the
 * set of structures a release covers) and by *request round*, and numbers versions
 * per lane for display. Storage numbering is untouched.
 *
 * Pure — no Firestore.
 */

import type { RfqHubFileRelease, RfqPackage, RfqRequest, RfqRequestLine } from '../types';
import { structureSelectionLabel, structureSetKey, WHOLE_PROJECT_SELECTION_LABEL } from './structureSelectionLabel';
import { packageHubReleaseIds } from './rfqPackageReleases';

export interface RfqReleaseRoundLine {
  key: string;
  line: RfqRequestLine;
  /** `null` when the staged release behind this line was removed. */
  release: RfqHubFileRelease | null;
  laneKey: string;
  laneLabel: string;
  /** Version within this lane (1-based); `null` when the release is gone. */
  ordinal: number | null;
}

export interface RfqReleaseRound {
  request: RfqRequest;
  lines: RfqReleaseRoundLine[];
}

export interface RfqStagedLane {
  key: string;
  label: string;
  structureIds: string[];
  structureNames: string[];
  /** Unsent releases on this lane, newest first. */
  releases: RfqHubFileRelease[];
}

export interface RfqReleaseGroups {
  /** Request rounds, newest first. */
  rounds: RfqReleaseRound[];
  /** Releases nobody has been asked to quote yet, grouped by structure lane. */
  staged: RfqStagedLane[];
  /** Releases sent from the Suppliers tab before request rounds existed (have packages, no round). */
  legacy: RfqHubFileRelease[];
}

export interface GroupTimberReleasesInput {
  releases: ReadonlyArray<RfqHubFileRelease>;
  packages?: ReadonlyArray<RfqPackage>;
  requests?: ReadonlyArray<RfqRequest>;
}

const time = (d: Date | undefined) => (d instanceof Date ? d.getTime() : 0);

/** Lane key of a staged release — the structure set it covers. */
export function releaseLaneKey(release: Pick<RfqHubFileRelease, 'structureIds'>): string {
  return structureSetKey(release.structureIds ?? []);
}

/** "Feed Barn" / "Barn + Shop" / "Whole project". */
export function releaseLaneLabel(release: Pick<RfqHubFileRelease, 'structureIds' | 'structureNames'>): string {
  const ids = release.structureIds ?? [];
  if (ids.length === 0) return WHOLE_PROJECT_SELECTION_LABEL;
  const names = ids.map((id, i) => release.structureNames?.[i] || id);
  return structureSelectionLabel(names, { maxNamed: 3 });
}

/** Releases on the same lane and work type, oldest first. */
function laneSiblings(release: RfqHubFileRelease, releases: ReadonlyArray<RfqHubFileRelease>): RfqHubFileRelease[] {
  const key = releaseLaneKey(release);
  return releases
    .filter(r => r.workType === release.workType && releaseLaneKey(r) === key)
    .sort((a, b) => a.releaseVersionIndex - b.releaseVersionIndex);
}

/**
 * 1-based rank of `release` among releases covering the same structures. Three
 * independently staged structures are each v1; a second Sales Barn list is v2 of the
 * Sales Barn lane only.
 */
export function laneReleaseOrdinal(release: RfqHubFileRelease, releases: ReadonlyArray<RfqHubFileRelease>): number {
  const siblings = laneSiblings(release, releases);
  const idx = siblings.findIndex(r => (r.id && release.id ? r.id === release.id : r.releaseVersionIndex === release.releaseVersionIndex));
  return idx < 0 ? siblings.filter(r => r.releaseVersionIndex < release.releaseVersionIndex).length + 1 : idx + 1;
}

/** "v2" (per lane). */
export function laneReleaseVersionLabel(release: RfqHubFileRelease, releases: ReadonlyArray<RfqHubFileRelease>): string {
  return `v${laneReleaseOrdinal(release, releases)}`;
}

/** "Sales Barn · v2"; whole-project releases read just "v2". */
export function laneReleaseTitle(release: RfqHubFileRelease, releases: ReadonlyArray<RfqHubFileRelease>): string {
  const v = laneReleaseVersionLabel(release, releases);
  return (release.structureIds ?? []).length ? `${releaseLaneLabel(release)} · ${v}` : v;
}

/** True when no release on the same lane has a higher `releaseVersionIndex`. */
export function isLatestInLane(release: RfqHubFileRelease, releases: ReadonlyArray<RfqHubFileRelease>): boolean {
  const siblings = laneSiblings(release, releases);
  return siblings.length === 0 || siblings[siblings.length - 1].releaseVersionIndex <= release.releaseVersionIndex;
}

/** Release ids referenced by any request line. */
export function releaseIdsInRequests(requests: ReadonlyArray<RfqRequest> | undefined): Set<string> {
  const ids = new Set<string>();
  for (const r of requests ?? []) for (const l of r.lines) if (l.hubFileReleaseId) ids.add(l.hubFileReleaseId);
  return ids;
}

/** Release ids that already have at least one published package. */
export function releaseIdsWithPackages(packages: ReadonlyArray<RfqPackage> | undefined): Set<string> {
  const ids = new Set<string>();
  for (const p of packages ?? []) for (const id of packageHubReleaseIds(p)) ids.add(id);
  return ids;
}

/** Staged releases no supplier has been asked to quote (no package, not in a round). */
export function unsentStagedReleases(input: GroupTimberReleasesInput): RfqHubFileRelease[] {
  const inRounds = releaseIdsInRequests(input.requests);
  const published = releaseIdsWithPackages(input.packages);
  return input.releases.filter(r => !!r.id && !inRounds.has(r.id) && !published.has(r.id));
}

export function groupTimberReleases(input: GroupTimberReleasesInput): RfqReleaseGroups {
  const releases = input.releases.filter(r => r.workType === 'timber_rfq');
  const byId = new Map<string, RfqHubFileRelease>();
  for (const r of releases) if (r.id) byId.set(r.id, r);
  const inRounds = releaseIdsInRequests(input.requests);
  const published = releaseIdsWithPackages(input.packages);

  const rounds: RfqReleaseRound[] = [...(input.requests ?? [])]
    .filter(r => r.workType === 'timber_rfq')
    .sort((a, b) => time(b.sentAt) - time(a.sentAt))
    .map(request => ({
      request,
      lines: request.lines.map(line => {
        const release = byId.get(line.hubFileReleaseId) ?? null;
        const ids = line.structures.map(s => s.id);
        const laneKey = release ? releaseLaneKey(release) : structureSetKey(ids);
        const laneLabel = release
          ? releaseLaneLabel(release)
          : ids.length
            ? structureSelectionLabel(line.structures.map(s => s.name || s.id), { maxNamed: 3 })
            : WHOLE_PROJECT_SELECTION_LABEL;
        return {
          key: line.key,
          line,
          release,
          laneKey,
          laneLabel,
          ordinal: release ? laneReleaseOrdinal(release, releases) : null,
        };
      }),
    }));

  const stagedByLane = new Map<string, RfqStagedLane>();
  const legacy: RfqHubFileRelease[] = [];
  for (const r of releases) {
    if (!r.id) continue;
    if (inRounds.has(r.id)) continue;
    if (published.has(r.id)) {
      legacy.push(r);
      continue;
    }
    const key = releaseLaneKey(r);
    const lane = stagedByLane.get(key) ?? {
      key,
      label: releaseLaneLabel(r),
      structureIds: [...(r.structureIds ?? [])],
      structureNames: (r.structureIds ?? []).map((id, i) => r.structureNames?.[i] || id),
      releases: [],
    };
    lane.releases.push(r);
    stagedByLane.set(key, lane);
  }
  const staged = Array.from(stagedByLane.values()).map(lane => ({
    ...lane,
    releases: [...lane.releases].sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex),
  }));
  // Whole project last; otherwise by the newest release in the lane (newest lane first).
  staged.sort((a, b) => {
    if (a.structureIds.length === 0 && b.structureIds.length > 0) return 1;
    if (b.structureIds.length === 0 && a.structureIds.length > 0) return -1;
    return b.releases[0].releaseVersionIndex - a.releases[0].releaseVersionIndex;
  });
  legacy.sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex);

  return { rounds, staged, legacy };
}
